import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@uzverse/core";
import type { SettingDescriptor, SettingValue } from "../api/types.js";
import { ErrorNote, useErrorText } from "../components/common.js";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";

function SettingControl({
  setting,
  locale,
  onChange,
}: {
  setting: SettingDescriptor;
  locale: Locale;
  onChange: (value: SettingValue) => void;
}) {
  const { t } = useTranslation("web");
  const id = `setting-${setting.key}`;
  const { kind, value } = setting;
  const disabled = !setting.editable;
  switch (kind.type) {
    case "boolean":
      return (
        <label className="check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{value === true ? t("advanced.on") : t("advanced.off")}</span>
        </label>
      );
    case "number":
      return (
        <input
          id={id}
          type="number"
          disabled={disabled}
          value={typeof value === "number" ? value : ""}
          {...(kind.min !== undefined ? { min: kind.min } : {})}
          {...(kind.max !== undefined ? { max: kind.max } : {})}
          onChange={(e) => {
            if (e.target.value !== "") onChange(Number(e.target.value));
            else if (setting.nullable) onChange(null);
          }}
        />
      );
    case "enum":
      return (
        <select
          id={id}
          disabled={disabled}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {kind.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label[locale]}
            </option>
          ))}
        </select>
      );
    case "string":
      return (
        <input
          id={id}
          disabled={disabled}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "json":
      return (
        <code id={id} className="small">
          {String(value)}
        </code>
      );
  }
}

function describeValue(
  setting: SettingDescriptor,
  locale: Locale,
  on: string,
  off: string,
): string {
  const v = setting.default;
  if (typeof v === "boolean") return v ? on : off;
  if (setting.kind.type === "enum") {
    return setting.kind.options.find((o) => o.value === v)?.label[locale] ?? String(v);
  }
  return String(v);
}

/** Renders every module-declared advanced user setting from its descriptor. */
export function AdvancedSettingsPage() {
  const { t, i18n } = useTranslation("web");
  const errorText = useErrorText();
  const { services } = useSession();
  const locale = i18n.language as Locale;
  const settings = useLoad(() => services.api.listSettings());
  const [error, setError] = useState<string | null>(null);

  const replace = (next: SettingDescriptor) =>
    settings.setData((prev) => prev?.map((s) => (s.key === next.key ? next : s)) ?? prev);

  async function change(setting: SettingDescriptor, value: SettingValue) {
    setError(null);
    try {
      replace(await services.api.setSetting(setting.key, value));
    } catch (err) {
      setError(errorText(err));
    }
  }
  async function reset(setting: SettingDescriptor) {
    setError(null);
    try {
      replace(await services.api.resetSetting(setting.key));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const advanced = settings.data?.filter((s) => s.level === "advanced") ?? [];
  const groups = new Map<string, SettingDescriptor[]>();
  for (const s of advanced) {
    const module = s.key.split(".")[0] ?? s.key;
    groups.set(module, [...(groups.get(module) ?? []), s]);
  }

  return (
    <section aria-labelledby="advanced-title" className="stack">
      <h1 id="advanced-title">{t("advanced.title")}</h1>
      <p className="muted">{t("advanced.intro")}</p>
      <ErrorNote message={error ?? (settings.error ? errorText(settings.error) : null)} />
      {settings.data && advanced.length === 0 && <p className="muted">{t("advanced.empty")}</p>}
      {[...groups].map(([module, list]) => (
        <fieldset key={module} className="card stack">
          <legend>{module}</legend>
          {list.map((s) => (
            <div key={s.key} className="field">
              <label htmlFor={`setting-${s.key}`}>{s.label[locale]}</label>
              <SettingControl setting={s} locale={locale} onChange={(v) => void change(s, v)} />
              {s.description && <small className="muted">{s.description[locale]}</small>}
              <div className="row small muted">
                <span>
                  {t("advanced.default", {
                    value: describeValue(s, locale, t("advanced.on"), t("advanced.off")),
                  })}
                </span>
                {s.editable && s.value !== s.default && (
                  <button type="button" className="btn btn-quiet" onClick={() => void reset(s)}>
                    {t("advanced.reset")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </fieldset>
      ))}
    </section>
  );
}
